// MUI
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableSortLabel from "@mui/material/TableSortLabel";
import visuallyHidden from "@mui/utils/visuallyHidden";
import { useRef, useState } from "react";
import type { MouseEvent, FocusEvent } from "react";
import { Box } from "@mui/material";
import { Span } from "./Typography";
import EditImg from "../../images/edit.png";
import type { HeadCell, AppContext, Config } from "../../../types";

// ==============================================================

// ==============================================================
// TABLE HEAD COLUMN DATA

type Order = "asc" | "desc";

interface LITableHeadProps {
    order?: Order;
    orderBy?: string;
    onRequestSort?: (event: MouseEvent, property: string) => void;
    useMobileTables: boolean;
    headCells: HeadCell[];
    editable?: boolean;
    setContext: (nv: Partial<Config>) => void;
    context: AppContext;
    rowCount?: number;
}

const LITableHead = (props: LITableHeadProps) => {
    const { order, orderBy, onRequestSort, useMobileTables, headCells, editable, setContext, context } = props;
    const [editingText, setEditingText] = useState<boolean>(false);
    const createSortHandler = (property: string) => (event: MouseEvent) => {
        onRequestSort!(event, property);
    };
    const headerRef = useRef<HTMLDivElement>(null);
    const tableHeaders: Record<string, string> = context?.config.tableHeaders || {};

    return useMobileTables ? null : (
        <TableHead
            sx={{
                backgroundColor: `#FFF`,
            }}
        >
            <TableRow>
                {headCells.map(headCell => (
                    <TableCell
                        key={headCell.id}
                        colSpan={headCell.colSpan ?? 1}
                        padding={headCell.disablePadding ? `none` : `normal`}
                        sortDirection={orderBy === headCell.id ? order : false}
                        sx={{
                            color: `#333`,
                            textAlign: headCell.align || `left`,
                            padding: `15px 15px 15px 24px`,
                            "& .MuiButtonBase-root.MuiTableSortLabel-root.Mui-active": {
                                color: `#333`,
                            },
                            ...(headCell.sx ? headCell.sx : {}),
                        }}
                    >
                        <Box
                            ref={headCell.editable ? headerRef : null}
                            suppressContentEditableWarning
                            contentEditable={editingText}
                            onBlur={(v: FocusEvent<HTMLDivElement>) => {
                                setContext({
                                    tableHeaders: {
                                        ...tableHeaders,
                                        [headCell.editable as string]: (v.target as HTMLDivElement).innerHTML,
                                    },
                                });
                                setEditingText(false);
                            }}
                            sx={editingText ? { display: `block` } : { display: `none` }}
                        >
                            {tableHeaders[headCell.editable as string] || headCell.label}
                        </Box>
                        <TableSortLabel
                            sx={{
                                "&.edit": {
                                    cursor: `url(${EditImg}), auto`,
                                },
                                ...(editingText ? { display: `none` } : { display: `inline` }),
                                ...(!onRequestSort ? { "& svg": { color: `transparent` } } : {}),
                            }}
                            className={
                                editingText
                                    ? `li-table-head editingText`
                                    : editable && headCell.editable
                                      ? `li-table-head edit`
                                      : `li-table-head`
                            }
                            active={orderBy === headCell.id}
                            onClick={
                                editable && headCell.editable && !editingText
                                    ? () => {
                                          setEditingText(true);
                                          setTimeout(() => {
                                              headerRef.current!.focus();
                                          }, 0);
                                      }
                                    : onRequestSort
                                      ? createSortHandler(headCell.id)
                                      : () => {}
                            }
                            direction={orderBy === headCell.id ? order : `asc`}
                        >
                            {tableHeaders[headCell.editable as string] || headCell.label}
                            {orderBy === headCell.id ? (
                                <Span sx={visuallyHidden}>
                                    {order === `desc` ? `sorted descending` : `sorted ascending`}
                                </Span>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
};

export default LITableHead;
